import type {
  CreateSurgeonDto,
  PaginatedSurgeonsResponseDto,
  SurgeonsFilterParams,
  UpdateSurgeonDto,
} from '@peritasai/types';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { SurgeonEntity } from './surgeon.entity';
import { surgeonsRepository } from './repository';

export const surgeonsKeys = {
  all: ['surgeons'] as const,
  lists: () => [...surgeonsKeys.all, 'list'] as const,
  list: (filters: SurgeonsFilterParams) =>
    [...surgeonsKeys.lists(), { filters }] as const,
} as const;

export function useSurgeons(
  filters: SurgeonsFilterParams = {}
): UseQueryResult<PaginatedSurgeonsResponseDto & { surgeons: SurgeonEntity[] }> {
  return useQuery({
    queryKey: surgeonsKeys.list(filters),
    queryFn: () => surgeonsRepository.getSurgeonsPaginated(filters),
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
  });
}

export function useCreateSurgeon(): UseMutationResult<SurgeonEntity, Error, CreateSurgeonDto> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dto: CreateSurgeonDto) => surgeonsRepository.createSurgeon(dto),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: surgeonsKeys.lists() });
    },
  });
}

export function useUpdateSurgeon(): UseMutationResult<
  SurgeonEntity,
  Error,
  { id: string; dto: UpdateSurgeonDto }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdateSurgeonDto }) =>
      surgeonsRepository.updateSurgeon(id, dto),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: surgeonsKeys.lists() });
    },
  });
}
